import { METRIC_NATIVE_BASIS, ratioCeiling } from '../config/metricsConfig';
import { getDisclaimer } from '../config/disclaimers';
import markerStyles from '../components/DisclaimerMarker.module.css';

/**
 * Last non-null, finite y of a `[x, y][]` series, or null when there is
 * none — i.e. the *current* (latest reported) figure. Used for the main
 * chart's legend labels (useStockData's mainChartLabels) and the Key Facts
 * panel's live valuation/dividend-yield figures, which must read the same
 * number as the legend does.
 */
export const lastNonNullPoint = (series = []) => {
  for (let i = series.length - 1; i >= 0; i--) {
    const [t, v] = series[i] || [];
    if (v != null && Number.isFinite(v)) return [t, v];
  }
  return null;
};

export const lastNonNull = (series = []) => lastNonNullPoint(series)?.[1] ?? null;

/**
 * Like lastNonNull, but reads the series AT a reference date, ignoring any
 * points after it. Needed for any series that extends past today:
 * `buildRatioSeries` output, for instance, spans the metric's own forward
 * timestamps (with price flat-held past its last real point), so a plain
 * lastNonNull reads the ratio at the LAST forward estimate — e.g. VZ's 2030
 * projected EPS against today's price — instead of the current blended
 * figure. Pass the last real price timestamp as `asOfTs` to get "the ratio
 * as of the latest actual market data".
 */
export const lastNonNullAsOf = (series = [], asOfTs) => {
  for (let i = series.length - 1; i >= 0; i--) {
    const [t, v] = series[i] || [];
    if (t != null && t <= asOfTs && v != null && Number.isFinite(v)) return v;
  }
  return null;
};

/**
 * Aligns a quarterly-ish step series onto another series' timestamps,
 * forward-holding the latest known value (falling back to the earliest
 * available value for timestamps before the first step point).
 */
export const stepJoin = (baseSeries, stepSeries) => {
  if (!stepSeries.length) return baseSeries.map(([t]) => [t, null]);
  let i = 0;
  return baseSeries.map(([t]) => {
    while (i + 1 < stepSeries.length && stepSeries[i + 1][0] <= t) i++;
    return [t, stepSeries[i][1]];
  });
};

/**
 * Like stepJoin, but linearly interpolates between the two step points
 * surrounding each base timestamp instead of forward-holding — so a value
 * that only reports once a year (e.g. a metric total) varies smoothly
 * alongside a densely-sampled base series (e.g. daily price) instead of
 * jumping once per report. Falls back to holding flat outside the step
 * series' range (before the first point, or after the last).
 */
export const interpJoin = (baseSeries, stepSeries) => {
  if (!stepSeries.length) return baseSeries.map(([t]) => [t, null]);
  let i = 0;
  return baseSeries.map(([t]) => {
    while (i + 1 < stepSeries.length && stepSeries[i + 1][0] <= t) i++;
    const [t0, v0] = stepSeries[i];
    const next = stepSeries[i + 1];
    if (!next || t <= t0) return [t, v0];
    const [t1, v1] = next;
    return [t, v0 + (v1 - v0) * (t - t0) / (t1 - t0)];
  });
};

/**
 * Converts a value between per-share and total-company scale. A no-op when
 * both bases already match.
 */
export const convertBasis = (value, fromBasis, toBasis, shares) => {
  if (fromBasis === toBasis) return value;
  return fromBasis === 'total' ? value / shares : value * shares;
};

/**
 * Converts a whole [t, raw][] series from its native basis to a target
 * basis, joining `shares` onto the series' own timestamps via stepJoin so
 * a metric reported once a year still converts correctly even as shares
 * outstanding changes over time. A no-op copy when the bases already
 * match, matching convertBasis's own no-op-when-equal behavior.
 */
export const convertSeriesBasis = (series, nativeBasis, targetBasis, shares) => {
  if (nativeBasis === targetBasis) return series;
  const sharesAtSeries = stepJoin(series, shares);
  return series.map(([t, raw], i) => [t, convertBasis(raw, nativeBasis, targetBasis, sharesAtSeries[i][1])]);
};

/**
 * Builds the fair-value comparison line: metric × multiple, converted
 * through total-company scale so it works for any metric/anchor/scale
 * combination — normalize to total, apply the multiple (which already
 * encodes which anchor, EV or Market Cap, it's a ratio against), then
 * convert to the requested display scale. Each point also carries the raw
 * metric value (in the same display scale) for the 2-row tooltip.
 *
 * Output is aligned onto `price`'s own timestamps (interpolating the metric
 * between its sparser report dates, like buildRatioSeries already does),
 * not the metric's own timestamps, plus the metric's own points beyond
 * price's last one (its forecast region — see buildEstimatedPlotBand).
 * This alignment matters beyond just visual smoothness: Highcharts'
 * `tooltip.shared` mode only merges series into a single tooltip box when
 * they share the *exact* same x-value at the hovered point — with the
 * metric's own sparse (annual/report-date) timestamps, this series almost
 * never lined up with the dense daily Price series, which fractured the
 * shared tooltip into separate boxes, snapped to the wrong nearest point,
 * and showed the wrong date in the header (all symptoms of the same
 * underlying x-mismatch, not independent bugs — see the bug report this
 * fixed). `price` is optional and defaults to `[]` so existing callers that
 * don't need chart-aligned output (e.g. buildFairValueOverlay's band
 * construction, which only needs internal consistency across its own three
 * calls, not alignment to Price) are unaffected.
 */
/**
 * Maps each real report timestamp in `metricSeries` to the output timestamp
 * its point actually lands on once buildFairValueSeries aligns onto Price's
 * dense daily grid: a report date within price's range resamples onto its
 * *closest* price-day timestamp (interpJoin's own behavior — see interpJoin
 * and buildFairValueSeries' marker-suppression comment), while a report date
 * beyond price's last point (or with no price at all) keeps its own
 * timestamp. Returns a `Map<reportTimestamp, outputTimestamp>`.
 *
 * Shared so both buildFairValueSeries (which needs the *set* of output
 * timestamps, to keep the report-date markers) and buildDividendFairValueSeries
 * (which needs the *mapping*, to attach each real report's dividend/yield onto
 * exactly the aligned output point — never onto an interpolated in-between
 * point) derive it from one source and can't drift apart.
 */
export const mapReportTimestampsToOutput = (metricSeries, price = []) => {
  const priceTimestamps = price.map(([t]) => t);
  const lastPriceX = price.length ? price[price.length - 1][0] : -Infinity;
  const closestPriceTimestamp = (t) => {
    let lo = 0;
    let hi = priceTimestamps.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (priceTimestamps[mid] < t) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0 && Math.abs(priceTimestamps[lo - 1] - t) <= Math.abs(priceTimestamps[lo] - t)) {
      return priceTimestamps[lo - 1];
    }
    return priceTimestamps[lo];
  };
  return new Map(
    metricSeries.map(([t]) => [t, t <= lastPriceX && priceTimestamps.length ? closestPriceTimestamp(t) : t])
  );
};

export const buildFairValueSeries = (metricSeries, shares, multiple, displayScale, metric, price = []) => {
  const nativeBasis = METRIC_NATIVE_BASIS[metric];
  // No price to align onto (e.g. buildFairValueOverlay's band-construction
  // calls, which only need internal consistency across their own three
  // calls, not alignment to Price) — fall back to the metric's own
  // timestamps unchanged, exactly as before this alignment was added.
  const sourceSeries = price.length
    ? [...interpJoin(price, metricSeries), ...metricSeries.filter(([t]) => t > price[price.length - 1][0])]
    : metricSeries;

  // Aligning onto Price's dense daily timestamps (above) means most output
  // points are interpJoin's *interpolated* in-between values, not real
  // reported metric values — but the "{Metric} × Multiple" series still
  // carries a series-level triangle marker default (chartConfig.js) meant to
  // mark actual report dates, one per fiscal year/quarter. Left alone, that
  // default paints a triangle on every one of those ~2,000+ daily points
  // instead of just the handful of real ones. Suppress it per-point on every
  // timestamp that isn't a real report date, so only real report-date points
  // keep the marker. The TTM-bridge/analyst-estimate tail beyond `price`
  // (appended straight from metricSeries above) is always an exact match
  // here and keeps its triangles untouched.
  //
  // Report dates within price's range don't survive interpJoin under their
  // own timestamp, though — interpJoin resamples onto *price's* own
  // timestamps (see its docstring above), so a report date's output point is
  // whichever price-day timestamp interpJoin emitted for it, not the report
  // timestamp itself. Two independently-sourced date series essentially
  // never share an exact millisecond value even when they nominally fall on
  // "the same day" (different time-of-day, or a report date that isn't a
  // trading day at all), so checking `reportTimestamps.has(t)` against the
  // report timestamps directly (as this used to) matched almost nothing,
  // leaving every real report point without its triangle. Match each report
  // timestamp to its *closest* price-day timestamp instead, and mark that
  // one — this is the same timestamp interpJoin actually emits for it.
  // Report dates beyond price's last point are never touched by interpJoin
  // (they're appended straight from metricSeries, unchanged), so they keep
  // their own timestamp as-is; with no price at all (`price.length === 0`,
  // e.g. overlay-band construction), every report timestamp is left as-is
  // for the same reason, matching sourceSeries' own untouched metricSeries
  // timestamps.
  // The key is only added at all for interpolated points, not set to some
  // "off" sentinel on report points, so report points cleanly inherit the
  // series-level marker default (chartConfig.js) rather than risk Highcharts
  // treating an explicit-but-empty per-point `marker` as "no marker".
  const reportTimestamps = new Set(mapReportTimestampsToOutput(metricSeries, price).values());
  const markerOverride = (t) => (reportTimestamps.has(t) ? {} : { marker: { enabled: false } });

  // No basis conversion needed at all when the metric's native scale already
  // matches what's being displayed (e.g. EPS at perShare, Revenue at total) —
  // skip the shares lookup entirely rather than round-tripping through it, so
  // this stays robust when a symbol has no enterprise-value history (shares
  // === []). This is also the app's default view (EPS/perShare), so it must
  // not regress just because generalizing to other metrics needs shares.
  if (nativeBasis === displayScale) {
    return sourceSeries.map(([t, raw]) => ({ x: t, y: raw * multiple, rawValue: raw, ...markerOverride(t) }));
  }

  // Interpolate shares (interpJoin), do NOT step-hold them (stepJoin). When
  // `price` was passed, `sourceSeries` is the metric total interpolated onto
  // Price's dense daily grid — a smooth intra-year ramp. Step-holding shares
  // against that smooth numerator makes the per-share value sawtooth: it
  // ramps up all year (numerator rising, denominator flat), then drops off a
  // cliff at each fiscal-year boundary when the new (higher) share count
  // steps in. For a heavy diluter — e.g. a REIT like ADC that issues shares
  // continuously (its count went 40M→52M→67M→79M→95M over five years) — that
  // cliff is large and produces the jagged FY-to-FY jumps in the fair-value/
  // avg line. Shares outstanding actually grow gradually, so interpolating
  // between the annual anchors models reality far better and removes the
  // artificial boundary discontinuity. On the non-price path (overlay-band
  // construction, sourceSeries === metricSeries at real report dates) every
  // point lands on a report timestamp, where interp and step agree, so this
  // is a no-op there.
  const sharesAtMetric = interpJoin(sourceSeries, shares);
  return sourceSeries.map(([t, raw], i) => {
    const sh = sharesAtMetric[i][1];
    const totalValue = convertBasis(raw, nativeBasis, 'total', sh);
    const impliedTotal = totalValue * multiple;
    return {
      x: t,
      y: convertBasis(impliedTotal, 'total', displayScale, sh),
      rawValue: convertBasis(raw, nativeBasis, displayScale, sh),
      ...markerOverride(t),
    };
  });
};


/**
 * Builds the main chart's dividend overlay line — dividends × the SAME
 * multiple the currently-selected metric's fair-value line uses. This is NOT
 * a valuation line (no "price ÷ dividend" / P/Div framing and no dividend
 * yield — those are price-based figures that live on the ratio/valuation
 * cards). Its only purpose is to overlay the dividend stream on the same
 * visual scale as the fair-value line so the user can read the PAYOUT RATIO
 * (dividends vs the fundamental) and DIVIDEND GROWTH directly off the chart.
 *
 * Deliberately one point PER REAL REPORTED DIVIDEND — it is NOT aligned onto
 * Price's dense daily grid (no `price` arg to buildFairValueSeries), so it is
 * NOT interpolated between report dates. Interpolating a dividend manufactures
 * per-share amounts that were never actually paid; a step/line between the
 * genuine annual points is the honest representation and is what reads as
 * "dividend growth." Every output point is therefore a real report point and
 * carries `divPerShare` (the raw reported dividend per share) for the FY
 * tooltip row.
 */
export const buildDividendFairValueSeries = (dividendSeries, shares, multiple, displayScale) => {
  // No `price` arg on purpose: buildFairValueSeries then returns exactly one
  // point per real reported dividend (sourceSeries === dividendSeries), never
  // an interpolated daily grid. Dividends are natively per-share, so
  // dividendSeries values ARE the raw dividend-per-share.
  const base = buildFairValueSeries(dividendSeries, shares, multiple, displayScale, 'dividends');
  const divPerShareByTs = new Map(dividendSeries.map(([t, v]) => [t, v]));
  return base.map((p) => ({ ...p, divPerShare: divPerShareByTs.get(p.x) }));
};

/**
 * Builds the "actual" observed line: Price, Market Cap, EV, or EV/share,
 * depending on valueBasis and displayScale.
 */
export const buildActualSeries = (price, shares, netDebt, valueBasis, displayScale) => {
  if (valueBasis === 'marketCap' && displayScale === 'perShare') {
    return price;
  }
  const sharesAtPrice = stepJoin(price, shares);
  const netDebtAtPrice = stepJoin(price, netDebt);
  return price.map(([t, px], i) => {
    const sh = sharesAtPrice[i][1];
    const marketCap = px * sh;
    const total = valueBasis === 'enterpriseValue' ? marketCap + netDebtAtPrice[i][1] : marketCap;
    return [t, displayScale === 'perShare' ? total / sh : total];
  });
};

/**
 * Extends a [x,y][] series with extra timestamps beyond its own last point,
 * holding its last known y flat at each one — the same "hold the latest
 * known value" idiom stepJoin/interpJoin already use for points *before*
 * their range, just extending the *end* instead. Used to give the ratio
 * chart's value series (Price/Market Cap/EV) a defined value out to
 * whatever forecast horizon the metric series reaches, so the ratio line
 * can continue into the estimate region the same way buildFairValueSeries
 * already does for the main chart's fair-value line (which doesn't need
 * this — it has no "value" input to extend, only metric × multiple).
 * timestamps not already beyond the series' end are ignored (no
 * duplicate/earlier points inserted); a series with no points can't be
 * extended and is returned as-is.
 */
export const extendFlat = (series, timestamps) => {
  if (!series.length) return series;
  const lastPoint = series[series.length - 1];
  const [lastX, lastY] = lastPoint;
  const extra = timestamps.filter((t) => t > lastX).map((t) => [t, lastY]);
  return extra.length ? [...series, ...extra] : series;
};

/**
 * Builds one metric's ratio (or, in yield mode, its reciprocal-as-percent)
 * line for the ratio chart: the company's value (Price/Market Cap/EV,
 * always on a *total* basis — the ratio is scale-invariant, so it doesn't
 * follow the main chart's per-share/total toggle) divided by the metric's
 * total value. One point per price datapoint: the metric (which only
 * reports once a year/quarter) is linearly interpolated onto price's
 * denser timestamps rather than held flat, so the ratio moves smoothly
 * instead of jumping once per filing.
 *
 * Extends into the forecast region for as long as the metric series has
 * forward coverage (TTM bridge + analyst estimates) — mirroring the main
 * chart's fair-value line, which already runs out that far. The value
 * series itself has no forecast (price/shares/netDebt stop at "today"), so
 * it's held flat at its last known point for the extended timestamps via
 * extendFlat — the same choice buildEstimatedPlotBand already implies is
 * the "estimated, not actual" region. Metrics with no forward estimates
 * (fcf, ocf) simply produce no extra timestamps to extend to.
 *
 * Ratio mode deliberately has a narrower validity domain than yield mode.
 * A valuation multiple is only useful here while its denominator remains
 * positive and the result stays within that metric's plausible ceiling:
 * loss-making periods, interpolation bridges into/out of them, and
 * near-zero-denominator explosions are emitted as gaps. This application
 * is intended for stable-fundamental valuation; preserving an extreme or
 * negative multiple is less useful than keeping the normal valuation range
 * readable. Yield mode stays continuous through zero by default, because
 * metric/value has no denominator singularity there (value is price/market
 * cap/EV, never near zero) — but `excludeLossPeriods` (default false) lets
 * a user opt into the same non-positive/sign-crossing exclusion ratio mode
 * always applies, for someone who wants a purely non-negative-fundamentals
 * yield view instead. This only ever nulls points; it never introduces
 * ratio mode's separate extreme-multiple ceiling, which has no yield-mode
 * analog (yield's denominator, value, doesn't approach zero the way a
 * fundamental can). See ADR 0001's second update and its addendum on this
 * flag.
 */
export const buildRatioSeries = (
  price, shares, netDebt, valueBasis, metric, metricSeries, ratioMode, excludeLossPeriods = false,
) => {
  // Compare on the metric's own native basis. A per-share metric (EPS,
  // dividends) is divided into a per-share value line, so the share count
  // cancels inside the ratio: a total-basis comparison would multiply the
  // per-share metric by the shares series at the metric's own timestamps while
  // the value line uses the shares at each price date, and a share-count change
  // between filings would then move P/E. Total-basis metrics keep the total
  // comparison unchanged.
  const nativeBasis = METRIC_NATIVE_BASIS[metric];
  const ratioBasis = nativeBasis === 'perShare' ? 'perShare' : 'total';
  const priceDrivenValueSeries = buildActualSeries(price, shares, netDebt, valueBasis, ratioBasis);
  const valueSeries = extendFlat(priceDrivenValueSeries, metricSeries.map(([t]) => t));

  // Same "skip the shares lookup when no conversion is needed" shortcut as
  // buildFairValueSeries: when the metric already sits on ratioBasis this is a
  // no-op and the shares series is never read.
  const metricRatioSeries = convertSeriesBasis(metricSeries, nativeBasis, ratioBasis, shares);

  // Ratio mode always excludes non-positive/sign-crossing periods (see the
  // docstring above); yield mode only does so when the caller opts in via
  // `excludeLossPeriods`. Both share the exact same bridge-nulling logic —
  // the sign of the fundamental is what matters, and that's identical
  // whether the final value is expressed as a ratio or a yield.
  const excludeNonPositive = ratioMode !== 'yield' || excludeLossPeriods;

  let metricAtValue;
  if (!excludeNonPositive) {
    // Normal interpolation: finite and informative through a zero-crossing
    // fundamental, since yield has no denominator singularity there.
    metricAtValue = interpJoin(valueSeries, metricRatioSeries);
  } else if (!metricRatioSeries.length) {
    metricAtValue = valueSeries.map(([t]) => [t, null]);
  } else {
    // Interpolating a positive report into a negative one would manufacture
    // daily denominator values arbitrarily close to zero (ratio mode) or
    // simply misrepresent a real sign transition as a smooth ramp (yield
    // mode, opted in). Null the bridge unless both surrounding reports are
    // positive; exact positive report points remain valid, while the
    // interval immediately after one becomes a gap if the next report is
    // non-positive.
    let i = 0;
    metricAtValue = valueSeries.map(([t]) => {
      while (i + 1 < metricRatioSeries.length && metricRatioSeries[i + 1][0] <= t) i++;
      const [t0, v0] = metricRatioSeries[i];
      const next = metricRatioSeries[i + 1];
      if (!next || t <= t0) return [t, v0 > 0 ? v0 : null];
      const [t1, v1] = next;
      if (v0 <= 0 || v1 <= 0) return [t, null];
      return [t, v0 + (v1 - v0) * (t - t0) / (t1 - t0)];
    });
  }

  const ceiling = ratioCeiling(metric, valueBasis, ratioMode);
  return valueSeries.map(([t, val], i) => {
    const metricValue = metricAtValue[i][1];
    if (metricValue == null || val == null || val === 0) return [t, null];
    if (metric === 'dividends' && metricValue === 0) return [t, null];
    if (ratioMode === 'yield') return [t, (metricValue / val) * 100];

    const ratio = val / metricValue;
    return ratio > 0 && (ceiling == null || ratio <= ceiling) ? [t, ratio] : [t, null];
  });
};


/**
 * Human-readable name for an implied-estimate anchor metric, used in the
 * plot-band label and caption so the disclosed derivation is accurate for
 * whichever anchor the backend actually used. The backend sends the anchor
 * as the suffix of an `"implied:<anchor>"` estimateKind string:
 * `"implied:ebitda"` for FCF/OCF, `"implied:dividendHistory"` for dividends
 * (projected from the company's own regular-dividend history). Unknown
 * anchors fall back to the raw key.
 */
export const IMPLIED_ANCHOR_LABEL = {
  ebitda: 'EBITDA',
  dividendHistory: 'dividend history',
};

/**
 * Split an estimateKind string into its base kind and (for implied
 * estimates) the anchor it was derived from. `"implied:ebitda"` ->
 * `{ base: 'implied', anchor: 'ebitda' }`; `"consensus"` ->
 * `{ base: 'consensus', anchor: null }`. Tolerates a bare `"implied"` (no
 * anchor) for backward compatibility.
 */
export const parseEstimateKind = (kind = 'consensus') => {
  const [base, anchor = null] = String(kind).split(':');
  return { base, anchor };
};

/**
 * Renders a footnote number as a Unicode superscript string (e.g. 1 -> '¹',
 * 12 -> '¹²'). Exported for tests; `footnoteLink` below is what the plot-band
 * label actually uses.
 */
const SUPERSCRIPT_DIGITS = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
export const toSuperscript = (n) =>
  (n == null ? '' : String(n).split('').map((d) => SUPERSCRIPT_DIGITS[Number(d)] ?? d).join(''));

/**
 * Same footnote marker the React `DisclaimerMarker` renders next to chart
 * titles (superscript number, linking to `#disclaimer-<n>` in the page-bottom
 * footer), but as an HTML string: the plot-band label is plain Highcharts
 * text, not a React node, so we can't render `DisclaimerMarker` itself here.
 * The label is rendered with `useHTML: true` (see `buildEstimatedPlotBand`),
 * so a real `<a>` works and is clickable/scrolls to the footer exactly like
 * the title markers. Reuses `DisclaimerMarker`'s CSS module class for visual
 * consistency. Returns '' for an unknown id, same fail-quiet behavior as
 * `DisclaimerMarker`.
 */
const footnoteLink = (id) => {
  const disclaimer = getDisclaimer(id);
  if (!disclaimer) return '';
  const { number, short } = disclaimer;
  return `<a href="#disclaimer-${number}" class="${markerStyles.link}" aria-label="Footnote ${number}: ${short} (see disclaimers at bottom of page)" title="${short}">${toSuperscript(number)}</a>`;
};

/**
 * Styling for the estimated-region plot band. Always grey — a single band can
 * span a mix of consensus and implied forward points (e.g. an EPS chart whose
 * forward region also carries an implied dividend estimate), so a per-metric
 * consensus-vs-implied color would be misleading at the band level. The label
 * carries "Estimated" plus the `implied-estimates` footnote's number as a
 * clickable superscript marker (`disclaimers.js`); that self-scoping footnote
 * ("Where a forward point is marked as 'implied'…") is where the derivation
 * detail lives, so the band stays a concise, honest annotation.
 */
const ESTIMATED_BAND_STYLE = {
  color: 'rgba(128, 128, 128, 0.12)',
  text: () => `Estimated${footnoteLink('implied-estimates')}`,
  labelColor: '#888',
};

/**
 * A plotBand from the last actual price point to the last point of the
 * selected metric series, marking the region running on the TTM/estimate
 * bridge rather than actuals. Empty when there's nothing beyond the price
 * series (no estimate coverage for this metric, or too little data to
 * compare), or when `kind` is `'none'` (no forward point at all). The band is
 * always grey with a footnote-marked "Estimated" label regardless of whether
 * the forward point is analyst consensus or an implied heuristic — see
 * ESTIMATED_BAND_STYLE for why a per-metric color would be misleading here.
 */
export const buildEstimatedPlotBand = (price, metricSeries, kind = 'consensus') => {
  const { base, anchor } = parseEstimateKind(kind);
  if (base === 'none') return [];
  if (!price.length || !metricSeries.length) return [];
  const lastPriceX = price[price.length - 1][0];
  const lastMetricX = metricSeries[metricSeries.length - 1][0];
  if (lastMetricX <= lastPriceX) return [];
  const style = ESTIMATED_BAND_STYLE;
  let text = style.text();
  // For implied metrics, clarify the basis and that it's not analyst data.
  if (base === 'implied' && anchor) {
    const anchorLabel = IMPLIED_ANCHOR_LABEL[anchor] || anchor;
    text += ` <span style="font-size:9px;color:#999;">(not analyst data—derived from ${anchorLabel})</span>`;
  }
  return [{
    id: 'estimated-region',
    from: lastPriceX,
    to: lastMetricX,
    color: style.color,
    label: {
      text,
      useHTML: true,
      style: { color: style.labelColor, fontSize: '11px' },
      verticalAlign: 'top',
      y: 14,
    },
  }];
};

/**
 * Subtracts stock-based compensation (floored at 0, so a reported SBC
 * credit/reversal never adds TO fcf/ocf — it simply doesn't subtract) from
 * a FCF or OCF series, for the "Adjust for SBC" toggle (2026-10-30 —
 * `metrics.sbc`, SCORE_CARDS.md §1.6). EPS, EBITDA, net income, revenue,
 * and dividends are untouched by this toggle; it only ever transforms the
 * `fcf`/`ocf` series callers pass in here.
 *
 * Reported and TTM points are matched to `sbcSeries` by EXACT timestamp
 * (both are annual/TTM-bridge cadence, like every other reported-year
 * series in this file) and subtracted directly. Any point with no exact
 * `sbcSeries` match — every forward/estimate-year point, since
 * `metrics.sbc` carries none of its own (see dataService.js) — uses a
 * PROJECTED forward SBC instead: the LAST reported sbc/revenue ratio
 * (not a multi-year median, unlike every other implied-forward metric in
 * this codebase — SBC/revenue often trends hard as a company matures, and
 * a stale multi-year median would lag that trend) x that point's own
 * `revenueSeries` value, matched by exact timestamp. A point that can't be
 * adjusted either way (no sbc match AND no usable ratio/revenue) is left
 * UNCHANGED, never dropped or nulled — degrading to the reported value is
 * safer than fabricating a worse one.
 */
export const applySbcAdjustment = (series, sbcSeries, revenueSeries) => {
  if (!series?.length) return series;
  const sbcByDate = new Map(
    (sbcSeries || []).filter(([, v]) => typeof v === 'number' && Number.isFinite(v)).map(([t, v]) => [t, v]),
  );
  if (!sbcByDate.size) return series;
  const revenueByDate = new Map(
    (revenueSeries || []).filter(([, v]) => typeof v === 'number' && Number.isFinite(v)).map(([t, v]) => [t, v]),
  );

  // The last (latest-dated) reported sbc/revenue ratio — a single point,
  // not a median (see docstring). `null` when revenue at that same date is
  // missing or not positive, in which case every forward point below is
  // left unadjusted rather than projected off a meaningless ratio.
  const lastSbcEntry = [...sbcByDate.entries()].sort((a, b) => a[0] - b[0]).at(-1);
  let lastRatio = null;
  if (lastSbcEntry) {
    const [lastTs, lastSbc] = lastSbcEntry;
    const lastRevenue = revenueByDate.get(lastTs);
    if (typeof lastRevenue === 'number' && lastRevenue > 0) {
      lastRatio = lastSbc / lastRevenue;
    }
  }

  return series.map(([t, value]) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return [t, value];
    if (sbcByDate.has(t)) {
      return [t, value - Math.max(sbcByDate.get(t), 0)];
    }
    // Only points AFTER the last reported SBC are projected; an earlier point
    // with no SBC print is left unchanged.
    if (lastRatio != null && t > lastSbcEntry[0] && revenueByDate.has(t)) {
      const projectedSbc = lastRatio * revenueByDate.get(t);
      return [t, value - Math.max(projectedSbc, 0)];
    }
    return [t, value];
  });
};
